/**
 * Apps Portal — what the user sees when they log in: tile of accessible forms.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FilesIcon, ArrowRight, ListChecks, Database } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AppsPortal() {
    const { user } = useAuth();
    const [apps, setApps] = useState([]);
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: forms }, pendingRes] = await Promise.all([
                    api.get("/forms/my"),
                    api.get("/approvals/pending").catch(() => ({ data: [] })),
                ]);
                setApps(forms);
                setPending(pendingRes.data);
            } catch (e) { toast.error(extractError(e)); }
            finally { setLoading(false); }
        })();
    }, [user]);

    return (
        <PageContainer>
            <PageHeader
                overline={`Welcome, ${user?.name?.split(" ")[0] || ""}`}
                title="My apps"
                description="Forms and tools assigned to you. Click any tile to view and manage records."
                actions={
                    pending.length > 0 && (
                        <Link to="/approvals">
                            <Button variant="outline" className="rounded-sm relative" data-testid="approvals-cta">
                                <ListChecks size={16} className="mr-2" /> Approvals
                                <span className="ml-2 bg-destructive text-destructive-foreground rounded-sm px-1.5 py-0.5 text-[10px] font-mono">{pending.length}</span>
                            </Button>
                        </Link>
                    )
                }
                testid="portal-header"
            />

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : apps.length === 0 ? (
                <EmptyState
                    icon={FilesIcon}
                    title="No apps assigned yet"
                    description="Your administrator hasn't shared any forms with you yet."
                    testid="portal-empty"
                />
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="portal-tiles">
                    {apps.map((f) => (
                        <Link key={f.id} to={`/forms/${f.id}/records`} className="brut-card p-4 group" data-testid={`portal-tile-${f.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="border border-foreground p-1.5 rounded-sm bg-foreground text-background">
                                    <FilesIcon size={16} weight="duotone" />
                                </div>
                                <Badge variant="outline" className="rounded-sm font-mono text-[9px] capitalize">{f.layout_type || "single"}</Badge>
                            </div>
                            <h3 className="text-sm font-semibold tracking-tight mb-0.5 truncate">{f.form_name}</h3>
                            <div className="text-[10px] font-mono text-muted-foreground mb-2 truncate">{f.form_code}</div>
                            {f.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{f.description}</p>}
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono pt-2 border-t border-border">
                                <span>OPEN</span>
                                <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {(user?.role === "platform_admin" || user?.role === "org_admin") && (
                <div className="mt-12 brut-section p-8" data-testid="portal-admin-shortcuts">
                    <div className="label-overline mb-2">Admin shortcuts</div>
                    <h2 className="text-xl font-bold tracking-tight mb-6">Build & manage</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Link to="/forms" className="border border-border p-4 rounded-sm hover:bg-secondary transition-colors text-sm"><FilesIcon size={16} className="mb-2" weight="duotone" /><div>Forms</div></Link>
                        <Link to="/users" className="border border-border p-4 rounded-sm hover:bg-secondary transition-colors text-sm"><Database size={16} className="mb-2" weight="duotone" /><div>Users</div></Link>
                        <Link to="/groups" className="border border-border p-4 rounded-sm hover:bg-secondary transition-colors text-sm"><Database size={16} className="mb-2" weight="duotone" /><div>Groups</div></Link>
                        <Link to="/dashboard" className="border border-border p-4 rounded-sm hover:bg-secondary transition-colors text-sm"><Database size={16} className="mb-2" weight="duotone" /><div>Dashboard</div></Link>
                    </div>
                </div>
            )}
        </PageContainer>
    );
}
